import * as React from 'react';

import {
  Alert, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import { ReportPeriod, ReportUser } from 'bloggers-backend/lib/bloggers/domain';
import { ReportLine, ReportLineChart } from './ReportLineChart';

interface ReportPeriodsViewProps {
  users: ReportUser[] | undefined;
  periods: ReportPeriod[] | undefined;
  isLoading: boolean;
  error: unknown;
  chartTitle: string;
  chartTestId: string;
}

export function ReportPeriodsView({ users = [], periods, isLoading, error, chartTitle, chartTestId }: ReportPeriodsViewProps) {
  const lines: ReportLine[] = periods
    ? [
      { id: 'total', label: 'Total', counts: periods.map(row => row.count) },
      ...users.map(user => ({
        id: `user-${user.id}`,
        label: user.name,
        counts: periods.map(row => row.byUser[user.id] ?? 0),
      })),
    ]
    : [];

  return (
    <>
      {error instanceof Error && <Alert severity="error">{`Error: ${error.message}`}</Alert>}
      {isLoading && <CircularProgress />}
      {periods && periods.length > 0 && (
        <ReportLineChart
          title={chartTitle}
          testId={chartTestId}
          periodLabels={periods.map(row => row.period)}
          lines={lines}
        />
      )}
      {periods && (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ fontWeight: 'bold' }}>Period</TableCell>
                <TableCell style={{ fontWeight: 'bold' }} align="right">Total</TableCell>
                {users.map(user => (
                  <TableCell key={user.id} style={{ fontWeight: 'bold' }} align="right">{user.name}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {periods.map(row => (
                <TableRow key={row.period}>
                  <TableCell>{row.period}</TableCell>
                  <TableCell align="right" data-testid={`report-total-${row.period}`}>{row.count}</TableCell>
                  {users.map(user => (
                    <TableCell
                      key={user.id}
                      align="right"
                      data-testid={`report-user-${user.id}-${row.period}`}
                    >
                      {row.byUser[user.id] ?? 0}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
}
